import React, {
  ReactNode,
  useState,
  useRef,
  useLayoutEffect,
  useImperativeHandle,
} from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useComponentDidUpdate } from '../../hooks/useComponentDidUpdate';
import {
  BASE_CLASS_NAME,
  POPUP_MENU_CONTENT_MIN_RIGHT_INDENT,
} from './constants';
import { moveElToLeftIfHidden } from './helpers';

export interface PopupMenuProps extends React.PropsWithChildren<any> {
  id?: string;
  anchorEl: ReactNode;
  onToggle?: (isOn: boolean) => void;
  isDisabled?: boolean;
}

export interface PopupMenuMethods {
  toggleMenu: (isOn?: boolean) => void;
}

const PopupMenu: React.FC<PopupMenuProps> = React.forwardRef<
  PopupMenuMethods,
  PopupMenuProps
>(
  (
    { id, anchorEl, children, onToggle = () => {}, isDisabled = false },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const menuRef = useRef(null);
    const anchorRef = useRef(null);
    const contentRef = useRef(null);

    useImperativeHandle(ref, () => ({
      toggleMenu,
    }));

    useLayoutEffect(() => {
      if (isExpanded)
        moveElToLeftIfHidden(
          POPUP_MENU_CONTENT_MIN_RIGHT_INDENT,
          contentRef.current
        );
    }, [isExpanded]);

    useComponentDidUpdate(() => {
      onToggle(isExpanded);
    }, [isExpanded]);

    useClickOutside(
      menuRef,
      () => {
        if (isDisabled) return;
        setIsExpanded(false);
      },
      [isDisabled]
    );

    const onAnchorClick = () => {
      if (isDisabled) return;
      toggleMenu();
    };

    const toggleMenu = (isOn = !isExpanded) => {
      setIsExpanded(isOn);
    };

    return (
      <div id={id} className={BASE_CLASS_NAME} ref={menuRef}>
        <div
          ref={anchorRef}
          onClick={onAnchorClick}
          className={`${BASE_CLASS_NAME}__anchor`}
        >
          {anchorEl}
        </div>
        {isExpanded && (
          <div ref={contentRef} className={`${BASE_CLASS_NAME}__content`}>
            {children}
          </div>
        )}
      </div>
    );
  }
);

export default PopupMenu;
